import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import {
  useAnchoringDocuments,
  type UnanchoredDocumentRow,
} from "~/api/v2/useAnchoringDocuments";
import { useBulkSelection } from "~/hooks/useBulkSelection";
import { Button } from "~/atoms/button";
import { CenterSpinner } from "~/atoms/spinner";
import ErrorState from "~/molecules/ErrorState";
import { AnchoringTable, type AnchoringColumn } from "./AnchoringTable";
import CreateQuizzesDialog from "./CreateQuizzesDialog";
import { DateCell, TagsCell, TitleLinkCell, UserCell } from "./cells";

export const UnanchoredDocumentsTab = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { unanchored, isLoading, error } = useAnchoringDocuments();
  const selection = useBulkSelection(
    unanchored.map((row) => row.documentVersionId),
  );
  const [isCreateOpen, setCreateOpen] = useState(false);

  if (isLoading) return <CenterSpinner />;
  if (error) return <ErrorState />;

  const selectedDocuments = unanchored
    .filter((row) => selection.selectedIds.has(row.documentVersionId))
    .map((row) => ({
      documentId: row.documentId,
      documentVersionId: row.documentVersionId,
      title: row.title,
    }));

  const columns: AnchoringColumn<UnanchoredDocumentRow>[] = [
    {
      key: "title",
      header: t(($) => $.anchoring.columns.title),
      cell: (row) => (
        <TitleLinkCell
          documentId={row.documentId}
          documentVersionId={row.documentVersionId}
          title={row.title}
        />
      ),
    },
    {
      key: "owner",
      header: t(($) => $.anchoring.columns.owner),
      cell: (row) => <UserCell user={row.owner} />,
    },
    {
      key: "publisher",
      header: t(($) => $.anchoring.columns.publisher),
      cell: (row) => <UserCell user={row.publisher} />,
    },
    {
      key: "publishedAt",
      header: t(($) => $.anchoring.columns.publishedAt),
      cell: (row) => <DateCell value={row.publishedAt} />,
    },
    {
      key: "adoptedAt",
      header: t(($) => $.anchoring.columns.adoptedAt),
      cell: (row) => <DateCell value={row.adoptedAt} />,
    },
    {
      key: "tags",
      header: t(($) => $.anchoring.columns.tags),
      cell: (row) => <TagsCell tags={row.tags} />,
    },
  ];

  return (
    <div className="flex flex-col gap-4">
      <div className="flex justify-end">
        <Button
          disabled={selection.selectedIds.size === 0}
          onClick={() => setCreateOpen(true)}
        >
          {t(($) => $.anchoring.actions.createQuizzes)}
        </Button>
      </div>
      <AnchoringTable
        columns={columns}
        rows={unanchored}
        getRowId={(row) => row.documentVersionId}
        emptyMessage={t(($) => $.anchoring.empty.unanchored)}
        selection={selection}
      />
      {isCreateOpen && (
        <CreateQuizzesDialog
          documents={selectedDocuments}
          open={isCreateOpen}
          onOpenChange={setCreateOpen}
          onCreated={() => {
            selection.clearSelection();
            void navigate("/anchoring/quizzes");
          }}
        />
      )}
    </div>
  );
};
